import { Download, Eye, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "./ResourceBadges";
import { DECISION_LABEL, DELIVERY_META } from "./resourceApi";
import { fmtSize } from "@/components/files/fileUtils";
import { fmtDateTime } from "@/lib/intl";

export function VersionHistory({ versions, selectedId, onSelect, onDownload, canApprove, onDecide }) {
  if (!versions.length) return <div className="text-sm text-zinc-500" data-testid="versions-empty">No versions yet.</div>;
  return (
    <ul className="divide-y divide-zinc-100" data-testid="version-history">
      {versions.map((v) => (
        <li key={v.id} className={`py-3 ${selectedId === v.id ? "bg-zinc-50" : ""}`} data-testid={`version-row-${v.version_number}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm font-semibold">v{v.version_number}</span>
            <StatusBadge status={v.status} testId={`version-status-${v.version_number}`} />
            <span className="text-xs text-zinc-500">{v.author_name} · {fmtDateTime(v.created_at)}</span>
            <div className="ml-auto flex gap-1">
              {v.file && <Button size="sm" variant="ghost" className="h-7" onClick={() => onSelect(v)} data-testid={`version-preview-${v.version_number}`}><Eye className="h-3.5 w-3.5" /></Button>}
              {v.file && <Button size="sm" variant="ghost" className="h-7" onClick={() => onDownload(v)} data-testid={`version-download-${v.version_number}`}><Download className="h-3.5 w-3.5" /></Button>}
              {canApprove && v.status === "in_review" && (
                <>
                  <Button size="sm" className="h-7" onClick={() => onDecide(v, "approve")} data-testid={`version-approve-${v.version_number}`}>
                    <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Approve</Button>
                  <Button size="sm" variant="outline" className="h-7" onClick={() => onDecide(v, "changes_requested")}
                          data-testid={`version-request-changes-${v.version_number}`}>Request changes</Button>
                </>
              )}
            </div>
          </div>
          {v.change_summary && <div className="mt-1 text-sm text-zinc-700">{v.change_summary}</div>}
          <div className="mt-1 text-2xs text-zinc-500">
            {v.file ? <>{v.file.filename} · {fmtSize(v.file.size)} · SHA-256 <span className="font-mono">{v.file.sha256.slice(0, 16)}…</span></> : "Link resource (no file)"}
            {v.approved_by_name && <> · approved by {v.approved_by_name} {fmtDateTime(v.approved_at)}</>}
          </div>
        </li>
      ))}
    </ul>
  );
}

export function ApprovalHistory({ approvals }) {
  if (!approvals.length) return <div className="text-sm text-zinc-500">No review decisions yet.</div>;
  return (
    <ul className="divide-y divide-zinc-100" data-testid="approval-history">
      {approvals.map((a) => (
        <li key={a.id} className="py-2.5 text-sm" data-testid={`approval-row-${a.id}`}>
          <span className="font-medium">{DECISION_LABEL[a.decision] || a.decision}</span>
          <span className="text-zinc-500"> · v{a.version_number} · {a.by_name} · {fmtDateTime(a.at)}</span>
          {a.self_approved && <span className="ml-2 rounded bg-amber-50 px-1.5 py-0.5 text-2xs text-amber-800">approved by its author</span>}
          {a.notes && <div className="mt-0.5 whitespace-pre-wrap text-zinc-700">{a.notes}</div>}
        </li>
      ))}
    </ul>
  );
}

export function UsageHistory({ usage, count }) {
  if (!count) return <div className="text-sm text-zinc-500">Not sent to any clients yet.</div>;
  return (
    <div data-testid="usage-history">
      <div className="mb-2 text-xs text-zinc-500">Sent {count} time{count === 1 ? "" : "s"}{usage.length < count ? ` — latest ${usage.length} shown` : ""}.</div>
      <ul className="divide-y divide-zinc-100">
        {usage.map((u) => (
          <li key={u.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
            <Link className="font-medium hover:underline" to={`/clients/${encodeURIComponent(u.client_email)}${u.canonical_client_id ? `?as=${u.canonical_client_id}` : ""}`}>
              {u.client_name || u.client_email}</Link>
            <span className="font-mono text-xs text-zinc-500">v{u.version_number}</span>
            <span className={`text-xs ${DELIVERY_META[u.delivery_status]?.tone || ""}`}>{DELIVERY_META[u.delivery_status]?.label || u.delivery_status}</span>
            <span className="text-xs text-zinc-500">{u.assigned_by_name} · {fmtDateTime(u.assigned_at)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function AuditList({ items }) {
  if (!items) return null;
  return (
    <ul className="divide-y divide-zinc-100 text-xs" data-testid="resource-audit-list">
      {items.map((e) => (
        <li key={e.id} className="flex flex-wrap gap-2 py-1.5">
          <span className="font-mono text-zinc-500">{fmtDateTime(e.at)}</span>
          <span className="font-medium text-zinc-800">{e.action}</span>
          <span className="text-zinc-600">{e.actor_name || (e.actor_type === "client" ? "Client (private link)" : "System")}</span>
        </li>
      ))}
    </ul>
  );
}
